import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { formatEatDateTime } from '@wodi/domain';
import type { ImportJobView, ImportKind, ImportStatus, StartImportBody } from '@wodi/shared';
import {
  Button,
  Cell,
  Column,
  FileField,
  formatFileSize,
  LoadingBlock,
  Notice,
  PageHeader,
  RadioGroup,
  Row,
  Select,
  Table,
  TableBody,
  TableHeader,
  Tag,
  TextField,
} from '@wodi/ui';
import { useState } from 'react';

import { ApiError } from '../api/client.ts';
import { can, sessionQuery } from '../api/session.ts';
import {
  codeSystemsQuery,
  importKindsQuery,
  importsQuery,
  sha256Hex,
  startImport,
} from '../api/terminology.ts';
import { useOnline } from '../hooks/use-online.ts';
import {
  emptyOpeningStockDraft,
  emptyPriceListDraft,
  OpeningStockFields,
  openingStockParameters,
  openingStockProblems,
  PriceListFields,
  priceListParameters,
  priceListProblems,
} from '../imports/import-parameters.tsx';
import { templateHref } from '../imports/template.ts';
import { uploadFile, type UploadProgress } from '../imports/upload.ts';
import styles from '../patients/patients.module.css';
import { SettingsPage } from './settings-nav.tsx';

// Settings > Imports (plan §6.3): the list of imports and a new import. Each import's
// preview, progress and report are on its own page.

function message(error: unknown, fallback: string): string {
  return error instanceof ApiError ? error.message : fallback;
}

export const IMPORT_STATUS_LABELS: Record<ImportStatus, string> = {
  uploading: 'Uploading',
  uploaded: 'Uploaded',
  validating: 'Checking rows',
  validated: 'Ready to import',
  committing: 'Importing',
  committed: 'Imported',
  failed: 'Failed',
  cancelled: 'Cancelled',
};

export function importStatusTag(status: ImportStatus) {
  const state =
    status === 'committed'
      ? 'done'
      : status === 'cancelled'
        ? 'neutral'
        : status === 'failed'
          ? 'failed'
          : status === 'validating' || status === 'committing'
            ? 'active'
            : 'pending';
  return <Tag state={state}>{IMPORT_STATUS_LABELS[status]}</Tag>;
}

const VERSION_PATTERN = /^\S.{0,63}$/;
const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/** What an entry point elsewhere (a price list, a store) chose before opening the form. */
export interface ImportPreset {
  kind?: ImportKind | undefined;
  priceListId?: string | undefined;
  storeId?: string | undefined;
}

function NewImport({ onDone, preset }: { onDone: () => void; preset: ImportPreset }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const online = useOnline();
  const { data: session } = useSuspenseQuery(sessionQuery);
  const kinds = useQuery(importKindsQuery);
  const [chosenKind, setChosenKind] = useState<ImportKind | null>(preset.kind ?? null);
  const [priceDraft, setPriceDraft] = useState(() =>
    emptyPriceListDraft(preset.priceListId ?? null),
  );
  const [stockDraft, setStockDraft] = useState(() =>
    emptyOpeningStockDraft(preset.storeId ?? null, new Date()),
  );
  const kind = kinds.data?.items.find((k) => k.kind === chosenKind) ?? kinds.data?.items[0];
  const format = kind?.formats[0];
  const isTerminology = kind?.kind === 'terminology';
  const systems = useQuery({ ...codeSystemsQuery, enabled: isTerminology });
  const [system, setSystem] = useState<string | null>(null);
  const [version, setVersion] = useState('');
  const [releaseDate, setReleaseDate] = useState('');
  const [sourceReference, setSourceReference] = useState('');
  const isPreauthList = kind?.kind === 'sha_preauth_list';
  const [file, setFile] = useState<File | null>(null);
  const [tried, setTried] = useState(false);
  const [progress, setProgress] = useState<UploadProgress | null>(null);

  const publisher = systems.data?.isReferencePublisher ?? false;
  const loadable = (systems.data?.items ?? []).filter((s) => s.owner === 'tenant' || publisher);
  const maxBytes = kind?.maxBytes ?? 0;

  const problems = {
    system:
      isTerminology && system === null
        ? 'Choose the code system this file is a version of.'
        : undefined,
    version:
      !isTerminology || VERSION_PATTERN.test(version.trim())
        ? undefined
        : 'Enter the version as the publisher names it.',
    releaseDate:
      !isTerminology || releaseDate.trim() === '' || DATE_PATTERN.test(releaseDate.trim())
        ? undefined
        : 'Write the date as YYYY-MM-DD, or leave it blank.',
    sourceReference:
      !isPreauthList || sourceReference.trim().length >= 3
        ? undefined
        : 'Name the official file and its release, as SHA published it.',
    file:
      file === null
        ? 'Choose the file to import.'
        : file.size === 0
          ? 'The file is empty.'
          : file.size > maxBytes
            ? `The file is larger than ${formatFileSize(maxBytes)}, the most one import can take.`
            : undefined,
  };
  const now = new Date();
  const kindProblems =
    kind?.kind === 'price_list'
      ? priceListProblems(priceDraft, now)
      : kind?.kind === 'opening_stock'
        ? openingStockProblems(stockDraft, now)
        : {};
  const valid = [...Object.values(problems), ...Object.values(kindProblems)].every(
    (p) => p === undefined,
  );

  const run = useMutation({
    mutationFn: async () => {
      if (file === null || kind === undefined || format === undefined)
        throw new Error('form incomplete');
      const fileFields = {
        fileName: file.name,
        fileSizeBytes: file.size,
        fileSha256: await sha256Hex(file),
        clientRequestId: crypto.randomUUID(),
      };
      let body: StartImportBody;
      if (kind.kind === 'sha_preauth_list') {
        body = {
          kind: 'sha_preauth_list',
          format: 'wodi-sha-preauth-list-csv-v1',
          parameters: { sourceReference: sourceReference.trim() },
          ...fileFields,
        };
      } else if (kind.kind === 'patients') {
        body = {
          kind: 'patients',
          format: 'wodi-patients-csv-v1',
          parameters: {},
          ...fileFields,
        };
      } else if (kind.kind === 'price_list') {
        body = {
          kind: 'price_list',
          format: 'wodi-price-list-csv-v1',
          parameters: priceListParameters(priceDraft),
          ...fileFields,
        };
      } else if (kind.kind === 'opening_stock') {
        body = {
          kind: 'opening_stock',
          format: 'wodi-opening-stock-csv-v1',
          parameters: openingStockParameters(stockDraft),
          ...fileFields,
        };
      } else {
        if (system === null) throw new Error('form incomplete');
        body = {
          kind: 'terminology',
          format: 'wodi-terminology-csv-v1',
          parameters: {
            codeSystemKey: system,
            version: version.trim(),
            releaseDate: releaseDate.trim() === '' ? null : releaseDate.trim(),
          },
          ...fileFields,
        };
      }
      const job = await startImport(body);
      return uploadFile(job, file, setProgress);
    },
    onSuccess: async (job: ImportJobView) => {
      await queryClient.invalidateQueries({ queryKey: importsQuery.queryKey });
      onDone();
      await navigate({ to: '/settings/imports/$importId', params: { importId: job.id } });
    },
  });

  if (kinds.isPending || (isTerminology && systems.isPending))
    return <LoadingBlock label="Loading" rows={3} />;
  if (kind === undefined || format === undefined) {
    return (
      <Notice tone="info">
        You don't have permission to run any kind of import. Ask an administrator if you need it.
      </Notice>
    );
  }

  return (
    <form
      className={styles.inlineForm}
      aria-label="New import"
      onSubmit={(event) => {
        event.preventDefault();
        setTried(true);
        if (valid) run.mutate();
      }}
    >
      <RadioGroup
        label="What to import"
        value={kind.kind}
        options={(kinds.data?.items ?? []).map((k) => ({ id: k.kind, label: k.label }))}
        onChange={(value) => {
          setChosenKind(value as ImportKind);
        }}
      />
      {kind.kind === 'patients' ? (
        <p>
          Each row is registered as a patient at this facility, with a new patient number. The old
          number is kept, so staff can still find people by it. Possible duplicates are shown before
          anything is imported.
        </p>
      ) : null}
      {kind.kind === 'price_list' ? (
        <PriceListFields
          draft={priceDraft}
          onChange={setPriceDraft}
          showProblems={tried}
          canCreateServices={session !== null && can(session, 'catalogue.manage')}
        />
      ) : null}
      {kind.kind === 'opening_stock' ? (
        <OpeningStockFields draft={stockDraft} onChange={setStockDraft} showProblems={tried} />
      ) : null}
      {isPreauthList ? (
        <>
          <p>
            The services SHA must approve before they are given, prepared from SHA&apos;s official
            benefit package. The list is loaded as a new version; activate it in Settings, SHA.
          </p>
          <TextField
            label="Source"
            description="The official file's name and release."
            value={sourceReference}
            onChange={setSourceReference}
            {...(tried && problems.sourceReference !== undefined
              ? { errorMessage: problems.sourceReference }
              : {})}
          />
        </>
      ) : null}
      {isTerminology ? (
        <>
          <Select
            label="Code system"
            placeholder="Choose a code system"
            options={loadable.map((s) => ({ id: s.key, label: s.name }))}
            value={system}
            onChange={(v) => {
              setSystem(v as string);
            }}
            {...(tried && problems.system !== undefined ? { errorMessage: problems.system } : {})}
          />
          <div className={styles.row}>
            <TextField
              label="Version"
              description="As the publisher names it."
              value={version}
              onChange={setVersion}
              {...(tried && problems.version !== undefined
                ? { errorMessage: problems.version }
                : {})}
            />
            <TextField
              label="Release date (optional)"
              description="YYYY-MM-DD"
              value={releaseDate}
              onChange={setReleaseDate}
              {...(tried && problems.releaseDate !== undefined
                ? { errorMessage: problems.releaseDate }
                : {})}
            />
          </div>
        </>
      ) : null}
      <FileField
        label="File"
        description={`${format.label}, saved as CSV UTF-8. Up to ${formatFileSize(maxBytes)}.`}
        acceptedFileTypes={['.csv', 'text/csv']}
        file={file}
        onChange={setFile}
        {...(tried && problems.file !== undefined ? { errorMessage: problems.file } : {})}
      />
      <details>
        <summary>Columns in the file</summary>
        <ul>
          {format.columns.map((c) => (
            <li key={c.key}>
              <span className="wodi-id">{c.key}</span>
              {c.required ? ' (required): ' : ': '}
              {c.description}
            </li>
          ))}
        </ul>
        <p>
          <a href={templateHref(format.columns)} download={`${format.format}-template.csv`}>
            Download template
          </a>
        </p>
      </details>
      {progress === null || !run.isPending ? null : (
        <p role="status">
          {`Uploading, ${formatFileSize(progress.sentBytes)} of ${formatFileSize(progress.totalBytes)}`}
        </p>
      )}
      {run.error === null ? null : (
        <Notice tone="error">
          {run.error instanceof ApiError && run.error.code === 'offline'
            ? 'Upload paused: no connection to Wodi. Open the import from the list when you are back online and choose the same file to carry on.'
            : message(run.error, "The import didn't start. Try again.")}
        </Notice>
      )}
      <div className={styles.actions}>
        <Button
          type="submit"
          variant="primary"
          isDisabled={!online}
          isPending={run.isPending}
          pendingLabel="Uploading…"
        >
          Upload and check
        </Button>
        <Button variant="quiet" onPress={onDone}>
          Cancel
        </Button>
      </div>
      {online ? null : <p>Uploading needs a connection to Wodi.</p>}
    </form>
  );
}

export function ImportsPage({ preset = {} }: { preset?: ImportPreset }) {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const allowed = session !== null && can(session, 'imports.read');
  const online = useOnline();
  const imports = useQuery({ ...importsQuery, enabled: allowed });
  const [adding, setAdding] = useState(preset.kind !== undefined);

  if (!allowed) {
    return (
      <>
        <PageHeader title="Imports" />
        <Notice tone="info">
          You don't have permission to see imports. Ask an administrator if you need it.
        </Notice>
      </>
    );
  }

  return (
    <SettingsPage
      title="Imports"
      panel
      {...(adding
        ? {}
        : {
            actions: (
              <Button
                isDisabled={!online}
                onPress={() => {
                  setAdding(true);
                }}
              >
                New import
              </Button>
            ),
          })}
    >
      <p>
        Load data from a file. Every row is checked first; nothing is imported until you have seen
        the problems and chosen to import.
      </p>
      {adding ? (
        <NewImport
          preset={preset}
          onDone={() => {
            setAdding(false);
          }}
        />
      ) : null}
      {imports.isPending ? <LoadingBlock label="Loading imports" rows={3} /> : null}
      {imports.isError ? (
        <Notice
          tone="error"
          action={
            <Button dense onPress={() => void imports.refetch()}>
              Try again
            </Button>
          }
        >
          {message(imports.error, "Imports couldn't be loaded. Try again.")}
        </Notice>
      ) : null}
      {imports.isSuccess ? (
        <Table flat aria-label="Imports">
          <TableHeader>
            <Column isRowHeader>File</Column>
            <Column>Status</Column>
            <Column numeric>Rows</Column>
            <Column numeric>With problems</Column>
            <Column>Started</Column>
          </TableHeader>
          <TableBody
            items={imports.data.items}
            renderEmptyState={() => 'No imports yet. Start one with New import.'}
          >
            {(job) => (
              <Row id={job.id} href={`/settings/imports/${job.id}`}>
                <Cell>{job.fileName ?? 'File name hidden'}</Cell>
                <Cell>{importStatusTag(job.status)}</Cell>
                <Cell numeric>{job.totalRows.toLocaleString('en-KE')}</Cell>
                <Cell numeric>{job.invalidRows.toLocaleString('en-KE')}</Cell>
                <Cell>{formatEatDateTime(new Date(job.createdAt))}</Cell>
              </Row>
            )}
          </TableBody>
        </Table>
      ) : null}
    </SettingsPage>
  );
}
